import React, { useState, useEffect } from 'react'
import {
  Avatar,
  Button,
  Tag,
  Descriptions,
  Drawer,
  message,
  Image,
  Space,
  Typography
} from 'antd'
import {
  UserOutlined,
  InstagramOutlined,
  FacebookOutlined,
  CheckCircleOutlined,
  PhoneOutlined,
  MailOutlined
} from '@ant-design/icons'
import api from '../utils/api'
import { useAuth } from '../hooks/AuthContext'
import styled from 'styled-components'
import { PHOTO_URL } from '../utils/apiutils'
import { categories } from '../utils/utils'

const { Title, Text } = Typography

const Container = styled.div`
  max-width: 100%;
  overflow-x: hidden;
`

const MobileCard = styled.div`
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 16px;
  margin-bottom: 20px;
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  @media (max-width: 820px) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const CardTitle = styled(Title)`
  margin: 0 !important;
  font-size: 18px !important;
`
const StyledButton = styled(Button)`
  background-color: #f0f5ff;
  color: #0050b3;
  border: none;
  &:hover {
    background-color: #e6f7ff !important;
  }
`

const StyledTag = styled(Tag)`
  font-size: 14px;
  padding: 4px 12px;
  border-radius: 16px;
  margin-right: 8px;
  margin-top: 8px;
  border: none;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &.status-active {
    background-color: #e6f7ff;
    color: #0050b3;
  }

  &.status-closed {
    background-color: #fff1f0;
    color: #cf1322;
  }

  &.type-employee {
    background-color: #f0f5ff;
    color: #1d39c4;
  }

  &.type-experienceModel {
    background-color: #fff7e6;
    color: #d46b08;
  }

  &.type-reciprocalModel {
    background-color: #f6ffed;
    color: #389e0d;
  }
`
const ModelList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ModelItem = styled.div`
  display: flex;
  align-items: center;
`

const ModelInfo = styled.div`
  display: flex;
  justify-content: space-between;
  margin-left: 12px;
  flex: 1;
`

const ModelName = styled.h4`
  margin: 0 0 4px 0;
  font-size: 16px;
`

const ModelAvatar = styled(Avatar)`
  width: 56px;
  height: 56px;
`
const MatchTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
  background-color: #fcf5e8;
  padding: 8px;
  border-radius: 8px;
  svg {
    margin-right: 8px;
  }
`

const MatchedModels = () => {
  const { businessId } = useAuth()
  const [matchedRecruitments, setMatchedRecruitments] = useState([])
  const [selectedModel, setSelectedModel] = useState(null)
  const [drawerVisible, setDrawerVisible] = useState(false)
  const getCategoryName = (types) => {
    if (typeof types === 'string') {
      return types
        .split(',')
        .map((type) => {
          const category = categories.find((cat) => cat.type === type.trim())
          return category ? category.name : type.trim()
        })
        .join(', ')
    }
    return types
  }
  useEffect(() => {
    fetchMatchedRecruitments()
  }, [])

  const fetchMatchedRecruitments = async () => {
    try {
      const response = await api.get(
        `/api/business_B/${businessId}/matched-recruitments`
      )
      setMatchedRecruitments(response.data.data)
    } catch (error) {
      console.log('獲取已配對招聘信息失敗:', error)
      //   message.error('獲取已配對招聘信息失敗')
    }
  }

  const showModelDetails = (model) => {
    setSelectedModel(model)
    setDrawerVisible(true)
  }

  const renderRecruitmentType = (type) => {
    const typeMap = {
      employee: '員工',
      experienceModel: '體驗模特',
      reciprocalModel: '互惠模特'
    }
    return typeMap[type] || type
  }

  const renderMatchedRecruitment = (item) => (
    <MobileCard key={item._id}>
      <CardHeader>
        <StyledTag color='green'>
          {renderRecruitmentType(item.recruitmentType)}
        </StyledTag>
        <CardTitle level={4}>{item.title}</CardTitle>
      </CardHeader>
      <MatchTitle>
        <CheckCircleOutlined /> 已配對模特
      </MatchTitle>
      <ModelList>
        {item.matchedModels.map((model) => (
          <ModelItem key={model._id}>
            <ModelAvatar
              src={`${PHOTO_URL}${model.avatar}`}
              icon={<UserOutlined />}
            />
            <ModelInfo>
              <div>
                <ModelName>{model.name}</ModelName>
                <Text type='secondary'>
                  預約時間： {model.acceptedDate} - {model.acceptedTime}
                </Text>
              </div>
              <StyledButton type='link' onClick={() => showModelDetails(model)}>
                查看
              </StyledButton>
            </ModelInfo>
          </ModelItem>
        ))}
      </ModelList>
    </MobileCard>
  )

  return (
    <Container>
      {matchedRecruitments.map(renderMatchedRecruitment)}
      <Drawer
        title='模特詳細資料'
        placement='right'
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={window.innerWidth > 768 ? 600 : '100%'}
      >
        {selectedModel && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label='姓名'>
              {selectedModel.name}
            </Descriptions.Item>
            <Descriptions.Item label='用戶名'>
              {selectedModel.username}
            </Descriptions.Item>
            <Descriptions.Item label='電子郵件'>
              <Space>
                <MailOutlined />
                {selectedModel.email}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label='電話'>
              <Space>
                <PhoneOutlined />
                {selectedModel.phone}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label='皮膚狀態'>
              {(() => {
                switch (selectedModel.modelProfile.skinType) {
                  case 'combination':
                    return '混合性'
                  case 'dry':
                    return '偏乾'
                  case 'oily':
                    return '偏油'
                  default:
                    return selectedModel.modelProfile.skinType
                }
              })()}
            </Descriptions.Item>
            <Descriptions.Item label='髮質'>
              {(() => {
                switch (selectedModel.modelProfile.hairType) {
                  case 'coarse':
                    return '粗硬'
                  case 'fine':
                    return '細軟'
                  case 'damaged':
                    return '受損'
                  case 'normal':
                    return '適中'
                  default:
                    return selectedModel.modelProfile.hairType
                }
              })()}
            </Descriptions.Item>
            <Descriptions.Item label='頭髮長度'>
              {(() => {
                switch (selectedModel.modelProfile.hairLength) {
                  case 'short':
                    return '短'
                  case 'medium':
                    return '中'
                  case 'long':
                    return '長'
                  default:
                    return selectedModel.modelProfile.hairLength
                }
              })()}
            </Descriptions.Item>
            <Descriptions.Item label='指甲狀況'>
              {(() => {
                switch (selectedModel.modelProfile.nailCondition) {
                  case 'brittle':
                    return '斷裂'
                  case 'normal':
                    return '正常'
                  case 'ingrown':
                    return '嵌甲'
                  default:
                    return selectedModel.modelProfile.nailCondition
                }
              })()}
            </Descriptions.Item>
            <Descriptions.Item label='身體狀況'>
              {(() => {
                switch (selectedModel.modelProfile.bodyCondition) {
                  case 'pregnant':
                    return '懷孕'
                  case 'breastfeeding':
                    return '哺乳'
                  case 'none':
                    return '無'
                  default:
                    return selectedModel.modelProfile.bodyCondition
                }
              })()}
            </Descriptions.Item>
            <Descriptions.Item label='過敏情況'>
              {selectedModel.modelProfile.isAllergic ? '是' : '否'}
              {selectedModel.modelProfile.allergyDetails &&
                `: ${selectedModel.modelProfile.allergyDetails}`}
            </Descriptions.Item>
            <Descriptions.Item label='希望參與項目類型'>
              {getCategoryName(
                selectedModel.modelProfile.preferredCategories[0]
              )}
            </Descriptions.Item>
            <Descriptions.Item label='體驗類型'>
              {(() => {
                switch (selectedModel.modelProfile.recruitmentType) {
                  case 'experienceModal':
                    return '體驗模特兒(純體驗)'
                  case 'reciprocalModel':
                    return '互惠模特兒(可幫忙宣傳與發文)'
                  default:
                    return selectedModel.modelProfile.recruitmentType
                }
              })()}
            </Descriptions.Item>
            <Descriptions.Item label='期望收費與否'>
              {(() => {
                switch (selectedModel.modelProfile.expectPayment) {
                  case 'yes':
                    return '是'
                  case 'no':
                    return '否'
                  case 'maybe':
                    return '不確定，看情況'
                  default:
                    return selectedModel.modelProfile.expectPayment
                }
              })()}
            </Descriptions.Item>
            <Descriptions.Item label='是否需要報酬'>
              {(() => {
                switch (selectedModel.modelProfile.needPayment) {
                  case 'yes':
                    return '是'
                  case 'no':
                    return '否'
                  case 'maybe':
                    return '不確定，看情況'
                  default:
                    return selectedModel.modelProfile.needPayment
                }
              })()}
            </Descriptions.Item>
            <Descriptions.Item label='模特類型'>
              {(() => {
                switch (selectedModel.modelProfile.modelType) {
                  case 'model':
                    return '素人模特兒'
                  case 'modelWithFans':
                    return '自帶粉絲模特兒'
                  case 'kol':
                    return 'KOL'
                  case 'influencer':
                    return '網紅'
                  case 'modelAndInfluencer':
                    return '模特兒兼網紅'
                  case 'other':
                    return '其他'
                  default:
                    return selectedModel.modelProfile.modelType
                }
              })()}
            </Descriptions.Item>
            <Descriptions.Item label='粉絲規模'>
              {(() => {
                switch (selectedModel.modelProfile.followerCount) {
                  case '1000':
                    return '1000-5000'
                  case '5000':
                    return '5000-10000'
                  case '10000':
                    return '10000-50000'
                  case '100000':
                    return '50000-100000'
                  case '1000000':
                    return '100000以上'
                  case 'other':
                    return '其他'
                  default:
                    return '無'
                }
              })()}
            </Descriptions.Item>
            <Descriptions.Item label='社交媒體'>
              <Space>
                <Tag icon={<InstagramOutlined />}>
                  {selectedModel.modelProfile.instagramUrl}
                </Tag>
                <Tag icon={<FacebookOutlined />}>
                  {selectedModel.modelProfile.facebookUrl}
                </Tag>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label='照片'>
              <Image.PreviewGroup>
                {selectedModel.modelProfile.photos.map((photo, index) => (
                  <Image
                    key={index}
                    src={`${PHOTO_URL}${photo}`}
                    alt={`照片 ${index + 1}`}
                    width={100}
                    height={100}
                    style={{
                      objectFit: 'cover',
                      marginRight: 8,
                      marginBottom: 8
                    }}
                  />
                ))}
              </Image.PreviewGroup>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </Container>
  )
}

export default MatchedModels
